import { useState, useRef, useEffect } from "react";
import { Wand2, Loader2, X, CheckCircle2, AlertTriangle, Shirt } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";

export function CinematicReprocessButton() {
  const [open, setOpen] = useState(false);
  const [force, setForce] = useState(false);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [rebrand, setRebrand] = useState(null); // dry-run preview for the WSTF rebrand mode
  const [checking, setChecking] = useState(false);
  const [limit, setLimit] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const pollRef = useRef(null);

  const running = job?.status === "RUNNING";
  const done = job?.status === "DONE";
  const failed = job?.status === "FAILED";
  const pct = job && job.total ? Math.round(((job.processed || 0) / job.total) * 100) : 0;

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const poll = async (jobId) => {
    try {
      const { data } = await api.get(`/admin/avatars/reprocess-cinematic/${jobId}`);
      setJob(data);
      if (data.status !== "RUNNING") {
        stopPoll();
        if (data.status === "DONE")
          toast.success(`Done — ${data.succeeded} regenerated, ${data.failed} failed, ${data.skipped} skipped`);
        else toast.error("Reprocess job failed");
      }
    } catch (_e) { /* transient — keep polling */ }
  };

  const dryRun = async () => {
    setChecking(true);
    try {
      const { data } = await api.get(`/admin/avatars/rebrand-wstf/preview`);
      setRebrand(data); setConfirmed(false); setLimit("");
    } catch (e) {
      toast.error(apiErr(e, "Could not count portraits"));
    } finally {
      setChecking(false);
    }
  };

  const start = async (mode = "all") => {
    setStarting(true);
    const params = mode === "rebrand_wstf" ? { mode, ...(limit ? { limit: Number(limit) } : {}) } : { force };
    try {
      const { data } = await api.post(`/admin/avatars/reprocess-cinematic`, null, { params });
      setJob({ ...data, processed: 0, succeeded: 0, failed: 0, skipped: 0 });
      stopPoll();
      pollRef.current = setInterval(() => poll(data.job_id), 2000);
    } catch (e) {
      toast.error(apiErr(e, "Could not start reprocess"));
    } finally {
      setStarting(false);
    }
  };

  const close = () => { if (!running) { setOpen(false); setJob(null); } };

  useEffect(() => () => stopPoll(), []);

  return (
    <>
      <button
        data-testid="admin-reprocess-avatars-btn"
        onClick={() => { setJob(null); setForce(false); setRebrand(null); setOpen(true); }}
        className="inline-flex items-center gap-2 rounded-lg bg-[#0f0f0f] border border-[rgba(212,175,55,0.35)] px-4 py-2.5 text-xs font-mono uppercase tracking-wider text-[#E5B842] hover:border-[#E5B842] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B842]/60"
        style={{ transition: "border-color 0.18s ease, transform 0.18s ease" }}
      >
        <Wand2 className="w-4 h-4" /> Regenerate Cinematic Portraits
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="reprocess-modal" onClick={close}>
          <div className="w-full max-w-md rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.25)] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-[#E5B842]" /> Cinematic Portraits
              </h3>
              <button data-testid="reprocess-close-btn" onClick={close} disabled={running} className="text-slate-500 hover:text-slate-200 disabled:opacity-30">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!job && (
              <>
                <p className="text-sm text-slate-400 mt-2">
                  Regenerate the cinematic black &amp; gold portrait for every player who has uploaded a photo, using the locked soccer style prompt.
                </p>
                <label className="flex items-center gap-2 mt-4 cursor-pointer select-none" data-testid="reprocess-force-label">
                  <input type="checkbox" data-testid="reprocess-force-checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                    className="w-4 h-4 accent-[#E5B842]" />
                  <span className="text-sm text-slate-300">Regenerate even up-to-date ones</span>
                </label>
                <div className="flex justify-end gap-2 mt-6">
                  <button onClick={close} className="px-4 py-2 rounded-lg text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-slate-200">Cancel</button>
                  <button data-testid="reprocess-start-btn" onClick={() => start("all")} disabled={starting}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E5B842] text-black text-xs font-mono font-bold uppercase tracking-wider hover:brightness-110 disabled:opacity-50">
                    {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Start
                  </button>
                </div>
                <RebrandSection rebrand={rebrand} checking={checking} onDryRun={dryRun} limit={limit} setLimit={setLimit}
                  confirmed={confirmed} setConfirmed={setConfirmed} starting={starting} onStart={() => start("rebrand_wstf")} />
              </>
            )}

            {job && (running || done || failed) && (
              <div className="mt-4" data-testid="reprocess-progress">
                <div className="flex items-center justify-between text-xs font-mono text-slate-400">
                  <span>{running ? "Processing…" : done ? "Complete" : "Failed"}</span>
                  <span className="tabular-nums">{job.processed || 0} / {job.total || 0}</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-[#1a1a1a] overflow-hidden">
                  <div className="h-full rounded-full bg-[#E5B842]" style={{ width: `${pct}%`, transition: "width 0.4s ease" }} />
                </div>
                {running && job.current_player && (
                  <div className="mt-2 text-[11px] font-mono text-slate-500 truncate">Current: {job.current_player}</div>
                )}
                <div className="grid grid-cols-3 gap-2 mt-4 text-center" data-testid="reprocess-done-summary">
                  <div className="rounded-lg bg-[#0a0a0a] border border-emerald-500/25 py-2">
                    <div className="text-lg font-black text-emerald-300 tabular-nums">{job.succeeded || 0}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Done</div>
                  </div>
                  <div className="rounded-lg bg-[#0a0a0a] border border-rose-500/25 py-2">
                    <div className="text-lg font-black text-rose-300 tabular-nums">{job.failed || 0}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Failed</div>
                  </div>
                  <div className="rounded-lg bg-[#0a0a0a] border border-slate-600/40 py-2">
                    <div className="text-lg font-black text-slate-300 tabular-nums">{job.skipped || 0}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Skipped</div>
                  </div>
                </div>
                {job.errors?.length > 0 && (
                  <div className="mt-3 rounded-lg bg-rose-500/10 border border-rose-500/25 p-3 text-[11px] text-rose-200 space-y-1">
                    {job.errors.map((er, i) => (
                      <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span className="break-words">{er.player || "batch"}: {er.reason}</span></div>
                    ))}
                  </div>
                )}
                {(done || failed) && (
                  <div className="flex justify-end mt-5">
                    <button data-testid="reprocess-finish-btn" onClick={() => { setOpen(false); setJob(null); }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E5B842] text-black text-xs font-mono font-bold uppercase tracking-wider hover:brightness-110">
                      <CheckCircle2 className="w-4 h-4" /> Close
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function RebrandSection({ rebrand, checking, onDryRun, limit, setLimit, confirmed, setConfirmed, starting, onStart }) {
  const n = rebrand ? (limit ? Math.min(Number(limit), rebrand.would_regenerate) : rebrand.would_regenerate) : 0;
  return (
    <div className="mt-6 pt-5 border-t border-[rgba(212,175,55,0.15)]" data-testid="rebrand-section">
      <div className="flex items-center gap-2 text-sm font-bold text-slate-100"><Shirt className="w-4 h-4 text-[#E5B842]" /> Rebrand σε WSTF</div>
      <p className="text-xs text-slate-400 mt-1">
        Ξαναδημιουργεί μόνο τα AI cinematic πορτρέτα με την παλιά φανέλα. Οι αρχικές φωτογραφίες των χρηστών δεν αλλάζουν ποτέ.
      </p>
      {!rebrand ? (
        <button data-testid="rebrand-dry-run-btn" onClick={onDryRun} disabled={checking}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E5B842]/50 text-[#E5B842] text-xs font-mono uppercase tracking-wider hover:border-[#E5B842] disabled:opacity-50">
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shirt className="w-4 h-4" />} Rebrand σε WSTF — μέτρηση
        </button>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat testid="rebrand-would-regenerate" value={rebrand.would_regenerate} label="Θα ξαναγίνουν" tone="text-[#E5B842]" />
            <Stat testid="rebrand-in-cooldown" value={rebrand.in_cooldown} label="Σε cooldown 60s" tone="text-slate-300" />
            <Stat testid="rebrand-already-done" value={rebrand.already_rebranded} label="Ήδη WSTF" tone="text-emerald-300" />
          </div>
          <label className="flex items-center justify-between gap-3 text-xs text-slate-400">
            Όριο (προαιρετικό)
            <input data-testid="rebrand-limit-input" type="number" min="1" value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="όλα" className="w-24 rounded-md bg-[#0a0a0a] border border-slate-700 px-2 py-1 text-slate-100 text-right font-mono" />
          </label>
          <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="rebrand-confirm-label">
            <input type="checkbox" data-testid="rebrand-confirm-checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#E5B842]" />
            <span className="text-xs text-slate-300">Επιβεβαιώνω την αναδημιουργία {n} πορτρέτων (κοστίζει AI budget).</span>
          </label>
          <div className="flex justify-end">
            <button data-testid="rebrand-start-btn" onClick={onStart} disabled={!confirmed || starting || n === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E5B842] text-black text-xs font-mono font-bold uppercase tracking-wider hover:brightness-110 disabled:opacity-40">
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shirt className="w-4 h-4" />} Rebrand {n}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ testid, value, label, tone }) {
  return (
    <div className="rounded-lg bg-[#0a0a0a] border border-slate-700/50 py-2" data-testid={testid}>
      <div className={`text-lg font-black tabular-nums ${tone}`}>{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}
